import React, { useState } from "react";
import { Warehouse, Store, Truck, Calculator, Building2, Users } from "lucide-react";
import { Reveal, Eyebrow, PageHeader, DirectorAvatar } from "../components/common";
import { CAREER_AREAS, JOB_ROLES, TOWNS, DIRECTORS } from "../mock";
import { STATES, STATES_DISTRICTS } from "../data/indiaLocations";
import { useSiteData } from "../context/SiteData";
import { submitCareer } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const AREA_ICONS = [Warehouse, Store, Truck, Calculator];
const GENERAL_APPLICATION = "General Application — no specific opening yet";
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const RESUME_EXTENSIONS = [".pdf", ".doc", ".docx"];

const EMPTY = {
  firstName: "", lastName: "", email: "", phone: "", qualification: "",
  street: "", state: "", city: "", pincode: "", role: "", location: "",
};

export default function Career() {
  const { toast } = useToast();
  const { data, loaded } = useSiteData();
  const [form, setForm] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const setPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, phone: digits }));
  };
  const setPincode = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: digits }));
  };
  const setState = (v) => setForm((f) => ({ ...f, state: v, city: "" }));

  const emailError = form.email && !EMAIL_RE.test(form.email);

  const districts = form.state ? (STATES_DISTRICTS[form.state] || []) : [];

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!RESUME_EXTENSIONS.includes(ext)) {
        toast({ title: "Unsupported file type", description: "Please upload a PDF or Word document (.pdf, .doc, .docx).", variant: "destructive" });
        e.target.value = "";
        setResume(null);
        return;
      }
    }
    setResume(file);
  };

  // Real, admin-editable vacancies (added via admin.html) take priority
  // when any exist; otherwise fall back to the site's default department
  // categories (unchanged from before this was wired up).
  const roles = loaded && data.vacancies.length
    ? [...data.vacancies.map((v) => v.title), GENERAL_APPLICATION]
    : JOB_ROLES;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.phone || !form.qualification || !form.state || !form.city || !form.role || !form.location) {
      toast({ title: "Please complete the required fields", description: "Fields marked with * are required.", variant: "destructive" });
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast({ title: "Invalid email address", description: "Please enter an email like example@mail.com or example@mail.co.in.", variant: "destructive" });
      return;
    }
    if (form.phone.length !== 10) {
      toast({ title: "Invalid phone number", description: "Contact number must be exactly 10 digits.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await submitCareer({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        qualification: form.qualification,
        address: form.street,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        jobRole: form.role,
        location: form.location,
      }, resume);
      toast({ title: "Application received", description: `Thanks ${form.firstName}! Your application stays on file for a matching role near ${form.location}.` });
      setForm(EMPTY);
      setResume(null);
    } catch (err) {
      toast({ title: "Couldn't submit application", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        crumb="Career"
        title="Build a career in genuine parts distribution"
        intro="Thirty years of warehouse and retail growth means steady, practical roles across seven districts of Northern Haryana — not a rotating cast of gig work."
      />

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-6">
        <Reveal>
          <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-8 h-full">
            <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center"><Building2 size={22} /></div>
            <h3 className="font-head font-bold text-xl mt-5">Why Rajiv Syndicate?</h3>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">Join a group recognised across 15 years for Best Warehouse, Best Technology User and Maximum Sales Growth — with three OEM authorisations behind the work.</p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-8 h-full">
            <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center"><Users size={22} /></div>
            <h3 className="font-head font-bold text-xl mt-5">Career Growth</h3>
            <p className="text-[var(--rs-muted)] mt-3 leading-relaxed">Steady roles across warehouse, retail, logistics and accounts — considered as soon as a matching opening comes up near you.</p>
          </div>
        </Reveal>
      </section>

      {/* Application form */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Apply</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Tell us about yourself</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5">Fill in the form below to apply — it stays on file when a matching role opens at a warehouse or outlet near you.</p></Reveal>

          <Reveal delay={180}>
            <form onSubmit={submit} className="bg-white border border-[var(--rs-border)] rounded-2xl p-8 mt-10 grid sm:grid-cols-2 gap-5">
              <Field label="First name" req><Input value={form.firstName} onChange={set("firstName")} placeholder="First name" /></Field>
              <Field label="Last name"><Input value={form.lastName} onChange={set("lastName")} placeholder="Last name" /></Field>
              <Field label="Email" req>
                <Input type="email" value={form.email} onChange={set("email")} placeholder="example@mail.com" />
                {emailError && <p className="text-[var(--rs-red)] text-xs mt-1.5">Enter a valid email, e.g. example@mail.com or example@mail.co.in</p>}
              </Field>
              <Field label="Contact number" req>
                <Input value={form.phone} onChange={setPhone} placeholder="10-digit phone number" inputMode="numeric" />
              </Field>
              <Field label="Higher qualification" req className="sm:col-span-2"><Input value={form.qualification} onChange={set("qualification")} placeholder="e.g. B.Com, ITI, Graduate" /></Field>
              <Field label="Street address" className="sm:col-span-2"><Input value={form.street} onChange={set("street")} placeholder="Street address" /></Field>
              <Field label="State" req>
                <Select value={form.state} onValueChange={setState}>
                  <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="City / District" req>
                <Select value={form.city} onValueChange={set("city")} disabled={!form.state}>
                  <SelectTrigger><SelectValue placeholder={form.state ? "Select city / district" : "Select a state first"} /></SelectTrigger>
                  <SelectContent>
                    {districts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Pin code"><Input value={form.pincode} onChange={setPincode} placeholder="6-digit pin code" inputMode="numeric" /></Field>
              <Field label="Job role" req>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger><SelectValue placeholder="Please select" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Preferred location" req>
                <Select value={form.location} onValueChange={set("location")}>
                  <SelectTrigger><SelectValue placeholder="Please select" /></SelectTrigger>
                  <SelectContent>
                    {TOWNS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Resume (PDF or Word)" className="sm:col-span-2">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeChange}
                  className="block w-full text-sm text-[var(--rs-muted)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#eef3fe] file:text-[var(--rs-blue)]"
                />
              </Field>
              <p className="sm:col-span-2 text-xs text-[var(--rs-muted)]">By submitting, you consent to Rajiv Syndicate Group storing and processing this information to consider you for a role.</p>
              <button type="submit" disabled={submitting} className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold sm:col-span-2 disabled:opacity-60">{submitting ? "Submitting…" : "Submit Application"}</button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Areas */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Where we hire</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4 max-w-3xl">Roles across warehouse, retail, logistics and accounts</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {CAREER_AREAS.map((a, i) => {
            const Icon = AREA_ICONS[i];
            return (
              <Reveal key={a.title} delay={i * 90}>
                <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center"><Icon size={22} /></div>
                  <h3 className="font-head font-bold text-lg mt-5">{a.title}</h3>
                  <p className="text-[var(--rs-muted)] text-sm mt-3 leading-relaxed">{a.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Directors */}
      <section className="bg-[var(--rs-bg)] border-t border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Directors</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Led by Sanjay Jain & Rajiv Jain</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Family leadership means decisions are made close to the warehouse floor, not through a distant head office.</p></Reveal>
          <div className="grid sm:grid-cols-2 gap-6 mt-10 max-w-3xl">
            {DIRECTORS.map((d) => (
              <div key={d.name} className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-6 flex items-center gap-4">
                <DirectorAvatar director={d} size={56} />
                <div><h3 className="font-head font-bold text-lg">{d.name}</h3><p className="text-[var(--rs-muted)] text-sm">Director</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, req, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-sm font-medium text-[var(--rs-ink)] mb-2 block">
        {label} {req && <span className="text-[var(--rs-red)]">*</span>}
      </Label>
      {children}
    </div>
  );
}
